"use client";

import { useCallback, useEffect, useState } from "react";
import Image from "@/components/Atoms/Image";
import {
	Carousel,
	CarouselContent,
	CarouselItem,
	useCarousel,
} from "@/components/ui/carousel";
import styles from "./index.module.scss";

type GalleryImage = {
	id?: string;
	url?: string;
	alt?: string | null;
	responsiveImage?: unknown;
};

type LimitedEditionGalleryProps = {
	images?: GalleryImage[];
	dotColor?: string;
};

function GalleryDots({ dotColor }: { dotColor?: string }) {
	const { api } = useCarousel();
	const [selected, setSelected] = useState(0);
	const [snaps, setSnaps] = useState<number[]>([]);

	const onSelect = useCallback(() => {
		if (!api) return;
		setSelected(api.selectedScrollSnap());
	}, [api]);

	useEffect(() => {
		if (!api) return;
		setSnaps(api.scrollSnapList());
		onSelect();
		api.on("reInit", () => {
			setSnaps(api.scrollSnapList());
			onSelect();
		});
		api.on("select", onSelect);
		return () => {
			api.off("select", onSelect);
		};
	}, [api, onSelect]);

	if (snaps.length <= 1) return null;

	return (
		<div className={styles.galleryDots}>
			{snaps.map((_, index) => (
				<button
					key={index}
					type="button"
					aria-label={`Go to slide ${index + 1}`}
					onClick={() => api?.scrollTo(index)}
					className={`${styles.galleryDot} ${
						index === selected ? styles.galleryDotActive : ""
					}`}
					style={dotColor ? { backgroundColor: dotColor } : undefined}
				/>
			))}
		</div>
	);
}

export function LimitedEditionGallery({
	images = [],
	dotColor,
}: LimitedEditionGalleryProps) {
	if (!images.length) return null;

	if (images.length === 1) {
		return (
			<div className={styles.gallerySingle}>
				<Image className={styles.galleryImage} data={images[0]} />
			</div>
		);
	}

	return (
		<>
			<div className="lg:hidden">
				<Carousel opts={{ align: "center", loop: images.length > 1 }}>
					<CarouselContent className="items-end">
						{images.map((image, index) => (
							<CarouselItem
								key={image.id ?? index}
								className="flex basis-[90%] justify-center sm:basis-[70%]"
							>
								<Image
									className={styles.galleryEmblaImage}
									data={image}
								/>
							</CarouselItem>
						))}
					</CarouselContent>
					<GalleryDots dotColor={dotColor} />
				</Carousel>
			</div>

			<div className={styles.galleryDesktop}>
				<div className={styles.gallery}>
					{images.map((image, index) => (
						<div
							key={image.id ?? index}
							className={styles.galleryItem}
						>
							<Image
								className={styles.galleryImage}
								data={image}
							/>
						</div>
					))}
				</div>
			</div>
		</>
	);
}
